import { useEffect, useState } from "react";
import { LuArrowRightLeft, LuCircleCheck, LuCircleX, LuClock, LuCpu } from "react-icons/lu";
import Pagination from "../Pagination/Pagination";
import LicenseService from "../../services/LicensesService";
import { formatDate } from "../../utils/date";

const associationTypeNames = {
   BINDING_CODE: "Código de vinculação",
   SERIAL_NUMBER: "Número de série",
   TOKEN_MQTT: "Token MQTT",
};

const associationActionNames = {
   createLicense: "Criação de licença",
   updateLicense: "Atualização de licença",
   updateBindingCode: "Atualização do código de vinculação",
   deleteLicense: "Exclusão de licença",
   blockLicense: "Bloqueio de licença",
   unblockLicense: "Desbloqueio de licença",
   licenseUpdated: "Licença atualizada",
   updateBackupEnable: "Atualização do backup",
};

const associationStatusPresentation = {
   pending: {
      label: "Pendente",
      Icon: LuClock,
      className: "text-amber-500 bg-amber-500/10",
   },
   in_progress: {
      label: "Em andamento",
      Icon: LuClock,
      className: "text-blue-500 bg-blue-500/10",
   },
   completed: {
      label: "Concluído",
      Icon: LuCircleCheck,
      className: "text-green-500 bg-green-500/10",
   },
   failed: {
      label: "Falhou",
      Icon: LuCircleX,
      className: "text-red-500 bg-red-500/10",
   },
};

function getAssociationStatus(association) {
   const statuses = [
      association.asyncOldValueDeletedStatus,
      association.asyncNewValueSavedStatus,
   ].filter(Boolean);

   if (statuses.includes("failed")) return "failed";
   if (statuses.includes("in_progress")) return "in_progress";
   if (statuses.includes("pending")) return "pending";
   return "completed";
}

function formatEventDate(value) {
   const date = new Date(value);
   return Number.isNaN(date.getTime()) ? "Data indisponível" : formatDate(date, true);
}

function TimelineEvent({ event }) {
   const isTransfer = event.eventType === "transfer";
   const Icon = isTransfer ? LuArrowRightLeft : LuCpu;
   const associationStatus = !isTransfer
      ? associationStatusPresentation[getAssociationStatus(event)]
      : null;
   const action = event.metadata?.action;

   return (
      <li className="relative pb-6! pl-8! last:pb-0!">
         <span className="absolute left-[-11px]! top-1 flex h-5 w-5 items-center justify-center rounded-full border! border-(--borderColor)! bg-(--containerBackgroundColor)!">
            <Icon size={12} />
         </span>
         <article className="rounded-(--borderRadius)! border! border-(--borderColor)! p-4!">
            <div className="flex flex-wrap items-center justify-between gap-2!">
               <div className="flex items-center gap-2! font-semibold!">
                  <Icon size={16} />
                  {isTransfer
                     ? "Transferência de licença"
                     : associationActionNames[action] ?? associationTypeNames[event.associationType] ?? event.associationType ?? "Associação"}
               </div>
               <time className="text-sm! opacity-60">
                  {formatEventDate(event.eventDate)}
               </time>
            </div>

            <div className="mt-3! flex flex-wrap items-center gap-x-5! gap-y-2! text-sm!">
               {isTransfer ? (
                  <>
                     <span>
                        <span className="opacity-60">Unidade de origem: </span>
                        {event.sourceUnit ?? "—"}
                     </span>
                     <span>
                        <span className="opacity-60">Unidade de destino: </span>
                        {event.targetUnit ?? "—"}
                     </span>
                     {event.reason && (
                        <span>
                           <span className="opacity-60">Motivo: </span>
                           {event.reason}
                        </span>
                     )}
                  </>
               ) : (
                  <>
                     <span>
                        <span className="opacity-60">Tipo: </span>
                        {associationTypeNames[event.associationType] ?? event.associationType ?? "—"}
                     </span>
                     <span>
                        <span className="opacity-60">De: </span>
                        {event.fromValue ?? "—"}
                     </span>
                     <span>
                        <span className="opacity-60">Para: </span>
                        {event.toValue ?? "—"}
                     </span>
                  </>
               )}
            </div>

            <div className="mt-3! flex flex-wrap items-center justify-between gap-2! text-sm!">
               <span className="opacity-60">
                  Solicitado por {event.agentData?.name ?? "Sistema"}
               </span>
               {associationStatus && (
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5! py-1! text-xs! font-semibold! ${associationStatus.className}`}>
                     <associationStatus.Icon size={14} />
                     {associationStatus.label}
                  </span>
               )}
            </div>
         </article>
      </li>
   );
}

export function LicenseHistoricPage({ license }) {
   const [events, setEvents] = useState([]);
   const [isLoading, setIsLoading] = useState(true);
   const [error, setError] = useState("");
   const [page, setPage] = useState(1);
   const [itemsPerPage, setItemsPerPage] = useState(6);
   const [pagination, setPagination] = useState({
      currentPage: 1,
      totalItems: 0,
      totalPages: 0,
   });

   useEffect(() => {
      let isCurrentRequest = true;

      async function fetchHistory() {
         setIsLoading(true);
         setError("");

         const response = await LicenseService.getPaginatedLicenseHistory(
            license.id,
            page,
            itemsPerPage,
         );

         if (!isCurrentRequest) return;

         if (response?.status >= 200 && response.status < 300) {
            const result = response.data;
            const pageInfo = result.pagination ?? {};
            setEvents(result.items ?? []);
            setPagination({
               currentPage: pageInfo.currentPage ?? page,
               totalItems: pageInfo.totalItems ?? 0,
               totalPages: pageInfo.totalPages ?? 0,
            });
         } else {
            setEvents([]);
            setPagination({ currentPage: page, totalItems: 0, totalPages: 0 });
            setError(
               response?.data?.message ??
               "Não foi possível carregar o histórico desta licença.",
            );
         }

         setIsLoading(false);
      }

      fetchHistory();
      return () => {
         isCurrentRequest = false;
      };
   }, [license.id, page, itemsPerPage]);

   return (
      <div className="fade-in">
         <section>
            <h3 className="font-bold! mb-4!">Histórico da licença</h3>
            {error && (
               <p className="mb-3! text-red-500" role="alert">{error}</p>
            )}

            {isLoading ? (
               <div className="space-y-3! py-2!">
                  {Array.from({ length: 4 }, (_, index) => (
                     <div key={index} className="h-24! rounded-(--borderRadius)! shimmer" />
                  ))}
               </div>
            ) : events.length ? (
               <ol className="relative ml-2! border-l! border-(--borderColor)!">
                  {events.map((event) => (
                     <TimelineEvent key={`${event.eventType}-${event.id}`} event={event} />
                  ))}
               </ol>
            ) : (
               <p className="py-8! text-center opacity-50">
                  Nenhum evento encontrado.
               </p>
            )}

            <Pagination
               currentPage={pagination.currentPage}
               maxPage={pagination.totalPages}
               rowsPerPage={itemsPerPage}
               total={pagination.totalItems}
               changePage={setPage}
               changeRowsPerPage={(value) => {
                  setPage(1);
                  setItemsPerPage(value);
               }}
            />
         </section>
      </div>
   );
}
